/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-plusplus */
import {
  DeleteOutlined,
  QuestionCircleOutlined,
  SaveFilled,
} from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Typography,
  Divider,
  Switch,
  Space,
  Card,
  Row,
  Col,
  Modal,
  message,
} from 'antd';
import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import TablePreview from '../TablePreview';

import SelectFile from 'common/components/SelectFile';
import { postPackageCompareService } from 'common/services/packages';
import { Item, Section } from 'common/services/packages/types';

const { Title } = Typography;
const { Option } = Select;

interface SectionFormValues {
  name: string;
  icon: string;
  type: 'string' | 'boolean';
  itemName?: string;
  itemDescription?: string;
  items: Item[];
  [key: `detail_${number}`]: string | boolean;
}

interface IPackageCompareDetailProps {
  tableList: Section[];
  numberCols?: number;
  onBack: () => void;
}

const PackageCompareDetail: React.FC<IPackageCompareDetailProps> = (
  { tableList, onBack, numberCols }
) => {
  const [numberCol, setNumberCol] = useState<number | null>(
    numberCols || null
  );
  const [sections, setSections] = useState<Section[]>(tableList);
  const [showDescriptionInputs, setShowDescriptionInputs] = useState<boolean[]>([]);
  const [editSection, setEditSection] = useState<Section | null>(null);
  const { t } = useTranslation();

  useMemo(() => {
    setSections(tableList);
  }, [tableList]);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<SectionFormValues>({
    defaultValues: {
      type: 'string',
      items: [],
      itemName: '',
      itemDescription: '',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const typeWatch = watch('type');

  const { mutate: createTableCompare } = useMutation(
    ['createPackage'],
    (data: Section[]) => postPackageCompareService(data),
    {
      onSuccess: () => {
        message.success(t('package.createSuccess'));
      },
      onError: () => {
        message.error(t('package.createError'));
      },
    }
  );

  const handleBack = () => {
    onBack();
  };

  const handleDeleteSection = (sectionIndex: number) => {
    Modal.confirm({
      title: t('packageCompare.confirmDeleteSection'),
      content: t('packageCompare.confirmDeleteDescription'),
      okText: t('packageCompare.delete'),
      cancelText: t('packageCompare.cancel'),
      onOk: () => {
        setSections((prev) => prev.filter((_, index) => index !== sectionIndex));
        message.success(t('packageCompare.deleteSectionSuccess'));
      },
    });
  };

  const addItem = () => {
    const values = getValues();
    const details: (string | boolean)[] = [];

    for (let i = 0; i < (numberCol || 0); i++) {
      const value = values[`detail_${i}`];
      details.push(values.type === 'boolean' ? Boolean(value) : String(value || ''));
    }

    const itemName = values.itemName || `Item ${fields.length + 1}`;
    const description = values.itemDescription || '';

    append({
      name: itemName,
      description,
      details,
    });

    setValue('itemName', '');
    setValue('itemDescription', '');
    for (let i = 0; i < (numberCol || 0); i++) {
      setValue(`detail_${i}`, values.type === 'boolean' ? false : '');
    }
    setShowDescriptionInputs([...showDescriptionInputs, false]);
  };

  // Initialize default row on mount
  useEffect(() => {
    addItem();
    setShowDescriptionInputs([false]);
  }, []);

  // Reset items when type or numberCol changes
  useEffect(() => {
    remove();
    setShowDescriptionInputs([]);
    for (let i = 0; i < (numberCol || 0); i++) {
      setValue(`detail_${i}`, typeWatch === 'boolean' ? false : '');
    }
    addItem();
  }, [typeWatch, numberCol, setValue, remove]);

  const handleAddSection = (values: SectionFormValues) => {
    const newSection: Section = {
      name: values.name,
      icon: values.icon,
      type: values.type,
      items: values.items,
    };

    if (editSection) {
      // Replace the edited section
      const updatedSections = sections.map((s) => (s.name === editSection.name ? newSection : s));
      setSections(updatedSections);
      setEditSection(null);
      message.success(t('packageCompare.editSuccess'));
    } else {
      // Normal add
      setSections([...sections, newSection]);
      message.success(t('packageCompare.addTableSuccess'));
    }

    reset({
      type: 'string',
      items: [],
      itemName: '',
      itemDescription: '',
    });
    setShowDescriptionInputs([]);
    addItem();
  };

  const confirmSaveSection = (values: SectionFormValues) => {
    const errorMessages: string[] = [];

    // Check for form errors
    if (errors.name) {
      errorMessages.push(t('packageCompare.error.nameRequired'));
    }
    if (errors.type) {
      errorMessages.push(t('packageCompare.error.typeRequired'));
    }
    if (fields.length === 0) {
      errorMessages.push(t('packageCompare.error.itemRequired'));
    }

    // Show all errors if any exist
    if (errorMessages.length > 0) {
      message.error(errorMessages.join('; '));
      return;
    }

    // Show confirmation modal if no errors
    Modal.confirm({
      title: t('packageCompare.confirmSave'),
      content: t('packageCompare.confirmDescription'),
      okText: t('packageCompare.save'),
      cancelText: t('packageCompare.cancel'),
      onOk: () => handleAddSection(values),
    });
  };

  const toggleDescriptionInput = (index: number) => {
    setShowDescriptionInputs((prev) => {
      const newInputs = [...prev];
      newInputs[index] = !newInputs[index];
      return newInputs;
    });
  };

  const handleSubmitTable = () => {
    if (isDirty) {
      createTableCompare(sections);
    } else {
      confirmSaveSection(getValues());
    }
  };

  const handleSelectEditSection = (section: Section) => {
    // Clear existing field array before filling new one
    remove();

    // Reset the form with the section data
    reset({
      name: section.name,
      icon: section.icon,
      type: section.type,
      items: [], // start empty, then append items
    });

    // Append section items into field array
    if (section.items && section.items.length > 0) {
      section.items.forEach((item) => {
        append({
          name: item.name,
          description: item.description || '',
          details:
            item.details?.length === (numberCol || 0)
              ? item.details
              : [
                // fill missing detail columns with empty values
                ...item.details,
                ...Array(Math.max((numberCol || 0) - item.details.length, 0)).fill(
                  section.type === 'boolean' ? false : ''
                ),
              ],
        });
      });
    }

    // Show description inputs if they had content
    setShowDescriptionInputs(section.items.map((i) => !!i.description));

    // Save edit state (if needed)
    setEditSection(section);

    message.info(t('packageCompare.editMode'));
  };

  return (
    <>
      <Row justify="space-between" style={{ marginBottom: 24 }}>
        <Title level={2}>{t('packageCompare.title')}</Title>
        <Button onClick={handleBack}>
          {t('packageCompare.back')}
        </Button>
      </Row>
      <div className="p-packageCompare_numberCol">
        <Form onFinish={(val) => setNumberCol(val.columns)} layout="inline">
          <Form.Item
            label={t('packageCompare.numberCol')}
            name="columns"
            rules={[{ required: true, message: t('packageCompare.colRequired') }]}
          >
            <InputNumber style={{ marginRight: 30 }} min={1} defaultValue={numberCol || 4} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              {t('packageCompare.apply')}
            </Button>
          </Form.Item>
        </Form>
      </div>
      <Divider orientation="left">{t('packageCompare.create')}</Divider>
      <Form layout="vertical" onFinish={handleSubmit(confirmSaveSection)}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div className="p-packageCompare_createSection">
            <div className="p-packageCompare_createSection_wrap">
              <Form.Item
                label={t('packageCompare.tableName')}
                required
                validateStatus={errors.name ? 'error' : ''}
                help={errors.name ? t('packageCompare.error.nameRequired') : ''}
              >
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => <Input {...field} />}
                />
              </Form.Item>
            </div>
            <div className="p-packageCompare_createSection_wrap">
              <Form.Item
                label={t('packageCompare.type')}
                required
                validateStatus={errors.type ? 'error' : ''}
                help={errors.type ? t('packageCompare.error.typeRequired') : ''}
              >
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select {...field}>
                      <Option value="string">{t('packageCompare.typeString')}</Option>
                      <Option value="boolean">{t('packageCompare.typeBoolean')}</Option>
                    </Select>
                  )}
                />
              </Form.Item>
            </div>
          </div>
          <Form.Item>
            <Controller
              name="icon"
              control={control}
              render={({ field: { value, onChange } }) => (
                <SelectFile
                  value={value}
                  name="icon"
                  handleSelect={(url) => onChange(url)}
                  handleDelete={() => onChange(undefined)}
                  title={t('packageCompare.uploadIcon')}
                />
              )}
            />
          </Form.Item>

          <Card title={t('packageCompare.addRow')}>
            {fields.map((field, index) => (
              <Row gutter={16} key={field.id} align="middle" style={{ marginBottom: 8, width: '100%' }}>
                <Col span={6}>
                  <Form.Item style={{ marginBottom: showDescriptionInputs[index] ? 8 : 0 }}>
                    <Controller
                      name={`items.${index}.name`}
                      control={control}
                      render={({ field: fieldItem }) => (
                        <Input
                          {...fieldItem}
                          suffix={(
                            <QuestionCircleOutlined
                              onClick={() => toggleDescriptionInput(index)}
                              style={{ cursor: 'pointer' }}
                              aria-label={`Toggle description for row ${index + 1}`}
                            />
                          )}
                        />
                      )}
                    />
                  </Form.Item>
                  {showDescriptionInputs[index] && (
                    <Form.Item style={{ marginBottom: 0 }}>
                      <Controller
                        name={`items.${index}.description`}
                        control={control}
                        render={({ field: fieldDescription }) => (
                          <Input.TextArea
                            {...fieldDescription}
                            rows={2}
                            placeholder={t('packageCompare.optional')}
                          />
                        )}
                      />
                    </Form.Item>
                  )}
                </Col>
                {Array.from({ length: numberCol || 0 }, (_, colIdx) => (
                  <Col span={3} key={colIdx}>
                    <Form.Item style={{ marginBottom: 0 }}>
                      <Controller
                        name={`items.${index}.details.${colIdx}`}
                        control={control}
                        render={({ field: fieldDetail }) => (typeWatch === 'boolean' ? (
                          <Switch
                            checked={!!fieldDetail.value}
                            onChange={(checked) => fieldDetail.onChange(checked)}
                          />
                        ) : (
                          <Input
                            value={typeof fieldDetail.value === 'boolean' ? '' : fieldDetail.value}
                            onChange={fieldDetail.onChange}
                          />
                        ))}
                      />
                    </Form.Item>
                  </Col>
                ))}
                <Row>
                  {index > 0 && (
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        remove(index);
                        setShowDescriptionInputs((prev) => prev.filter((_, i) => i !== index));
                      }}
                      danger
                      aria-label={`Delete row ${field.name}`}
                    >
                      {t('packageCompare.deleteRow')}
                    </Button>
                  )}
                  {index < 1 && (
                    <Button
                      icon={<SaveFilled />}
                      onClick={addItem}
                      type="dashed"
                      aria-label="Add new row"
                      style={{ marginLeft: 8 }}
                    >
                      {t('packageCompare.addRow')}
                    </Button>
                  )}
                </Row>
              </Row>
            ))}
          </Card>

          <Button type="primary" htmlType="submit" aria-label="Save section">
            {t('packageCompare.saveReviewTable')}
          </Button>
        </Space>
      </Form>
      <Divider orientation="left">{t('packageCompare.preview')}</Divider>
      <div className="p-packageCompare_preview">
        {sections.length > 0 && (
          <TablePreview
            sections={sections}
            numberCol={numberCol}
            onDelete={handleDeleteSection}
            onEdit={handleSelectEditSection}
          />
        )}
        <div className="p-packageCompare_preview_buttonWrap">
          <Button
            className="p-packageCompare_preview_button"
            onClick={() => handleSubmitTable()}
            disabled={!isDirty}
          >
            {t('packageCompare.save')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default PackageCompareDetail;
